/**
 * Dropdown hide/show from setting_controls.
 *
 * Layout (mapping_parent / belongs_to_id) stays elsewhere.
 * This unit only answers: should this row paint, given a selected option.
 *
 * Multiple controls combine with OR: any pass → show; all fail → hide; all skip → show.
 *
 * Non-null option id on an active control → pass or fail only (no skip).
 */

export type SettingControlLike = {
  belongs_to_platform_field_data?: number | string | null
  belongs_to_setting?: number | string | null
  belongs_to_setting_mapping?: number | string | null
  is_active?: boolean | null
  is_visible?: boolean | null
}

export type DropdownVisibilityContext = {
  siblings: unknown[]
  getRowId: (row: any) => number | null
  getOptionIds: (row: any) => number[]
  getSelectedIds: (row: any) => number[]
  getParentRefId: (control: SettingControlLike) => number | null
  getSiblingLookupIds: (row: any) => number[]
}

export type ChildSettingControlRenderOptions = {
  thisSideMappings?: unknown[]
  oppositeSideMappings?: unknown[]
  pendingMappings?: any[]
  savedMappings?: any[]
}

export const buildChildSettingControlRenderOptions = (
  thisSetting: any,
  oppositeSetting: any | null | undefined,
  pendingMappings: any[] = [],
  savedMappings: any[] = []
): ChildSettingControlRenderOptions => ({
  thisSideMappings: thisSetting?.mappings_data ?? [],
  oppositeSideMappings: oppositeSetting?.mappings_data ?? [],
  pendingMappings,
  savedMappings,
})

type ControlResult = 'pass' | 'fail' | 'skip'

export const toNumericId = (value: unknown): number | null => {
  if (value === null || value === undefined || value === '') {
    return null
  }
  if (typeof value === 'boolean') {
    return null
  }
  const numeric = typeof value === 'number' ? value : Number(`${value}`.trim())
  return Number.isFinite(numeric) ? numeric : null
}

export const uniqueNumericIds = (values: number[]): number[] =>
  Array.from(new Set(values.filter((id) => Number.isFinite(id))))

export const collectNumericIds = (value: unknown): number[] => {
  if (Array.isArray(value)) {
    return uniqueNumericIds(value.map(toNumericId).filter((id): id is number => id !== null))
  }
  const id = toNumericId(value)
  return id === null ? [] : [id]
}

export const combineSettingControlSiblingPools = (
  thisSide: unknown[] = [],
  oppositeSide: unknown[] = []
): unknown[] => [
  ...(Array.isArray(thisSide) ? thisSide : []),
  ...(Array.isArray(oppositeSide) ? oppositeSide : []),
]

export const isSettingControlObjectActive = (control: SettingControlLike): boolean => {
  if (control?.is_active === false) {
    return false
  }
  if (control?.is_visible === false) {
    return false
  }
  return true
}

export const getSettingControls = (row: any): SettingControlLike[] =>
  (Array.isArray(row?.setting_controls) ? row.setting_controls : []).filter(
    isSettingControlObjectActive
  )

export const getMappingOptionIds = (row: any): number[] =>
  uniqueNumericIds(
    (Array.isArray(row?.platformfielddata_set) ? row.platformfielddata_set : [])
      .map((option: any) => toNumericId(option?.id))
      .filter((id: number | null): id is number => id !== null)
  )

export const getChildOptionIds = (row: any): number[] =>
  uniqueNumericIds(
    (Array.isArray(row?.setting_options) ? row.setting_options : [])
      .map((option: any) => toNumericId(option?.id))
      .filter((id: number | null): id is number => id !== null)
  )

export const getMappingParentRefId = (control: SettingControlLike): number | null =>
  toNumericId(control?.belongs_to_setting_mapping)

export const getChildParentRefId = (control: SettingControlLike): number | null =>
  toNumericId(control?.belongs_to_setting)

export const getMappingSiblingLookupIds = (row: any): number[] => {
  const id = toNumericId(row?.id)
  const settingMappingId = toNumericId(row?.setting_mapping_id)
  const ids: number[] = []
  if (id !== null) {
    ids.push(id)
  }
  if (settingMappingId !== null && settingMappingId !== id) {
    ids.push(settingMappingId)
  }
  return ids
}

export const getChildSiblingLookupIds = (row: any): number[] => {
  const id = toNumericId(row?.id)
  return id === null ? [] : [id]
}

const pushNumericId = (ids: number[], value: unknown) => {
  collectNumericIds(value).forEach((id) => ids.push(id))
}

const collectIdsFromMappingDataRow = (row: any): number[] => {
  if (!row || typeof row !== 'object') {
    return []
  }

  const ids: number[] = []
  pushNumericId(ids, row.destination_platform_data?.id)
  pushNumericId(ids, row.destination_platform_data_id)
  pushNumericId(ids, row.destination_platform_data_str)
  if (typeof row.destination_platform_data === 'number') {
    pushNumericId(ids, row.destination_platform_data)
  }
  pushNumericId(ids, row.source_platform_data?.id)
  pushNumericId(ids, row.source_platform_data_id)
  pushNumericId(ids, row.source_platform_data_str)
  if (typeof row.source_platform_data === 'number') {
    pushNumericId(ids, row.source_platform_data)
  }
  return uniqueNumericIds(ids)
}

const findMappingBySettingMappingId = (rows: any[] = [], lookupId: number) =>
  rows.find(
    (row) => toNumericId(row?.setting_mapping_id) === lookupId || toNumericId(row?.id) === lookupId
  )

export const collectMappingSelectedOptionIds = (
  mapping: any,
  pendingMappings: any[] = [],
  savedMappings: any[] = []
): number[] => {
  const settingMappingId = toNumericId(mapping?.setting_mapping_id)
  const rowId = toNumericId(mapping?.id)
  const lookupIds = uniqueNumericIds(
    [settingMappingId, rowId].filter((id): id is number => id !== null)
  )
  if (!lookupIds.length) {
    return []
  }

  for (const lookupId of lookupIds) {
    const pending = findMappingBySettingMappingId(pendingMappings, lookupId)
    const pendingRows = pending?.fieldMappingDataSet ?? pending?.fieldmappingdata_set ?? []
    const pendingIds = (Array.isArray(pendingRows) ? pendingRows : []).flatMap(
      collectIdsFromMappingDataRow
    )
    if (pendingIds.length) {
      return uniqueNumericIds(pendingIds)
    }

    const saved = findMappingBySettingMappingId(savedMappings, lookupId)
    const savedRows = saved?.fieldmappingdata_set ?? saved?.fieldMappingDataSet ?? []
    const savedIds = (Array.isArray(savedRows) ? savedRows : []).flatMap(collectIdsFromMappingDataRow)
    if (savedIds.length) {
      return uniqueNumericIds(savedIds)
    }
  }

  return []
}

const tokenizeFormValue = (value: unknown): string[] => {
  if (value === null || value === undefined || value === '') {
    return []
  }
  if (Array.isArray(value)) {
    return value
      .flatMap((item) => tokenizeFormValue(item))
      .map((item) => `${item}`.trim())
      .filter(Boolean)
  }
  return `${value}`
    .split(',')
    .map((item) => item.trim())
    .filter(Boolean)
}

export const collectChildSelectedOptionIds = (child: any, formValue: unknown): number[] => {
  const options = Array.isArray(child?.setting_options) ? child.setting_options : []
  const ids: number[] = []

  tokenizeFormValue(formValue).forEach((token) => {
    const tokenId = toNumericId(token)
    if (tokenId !== null && options.some((option: any) => toNumericId(option?.id) === tokenId)) {
      ids.push(tokenId)
      return
    }

    const matched = options.find(
      (option: any) =>
        `${option?.name ?? ''}` === token || `${option?.display_name ?? ''}` === token
    )
    const matchedId = toNumericId(matched?.id)
    if (matchedId !== null) {
      ids.push(matchedId)
    }
  })

  return uniqueNumericIds(ids)
}

export const getChildFormValue = (
  child: any,
  formValues: Record<string, any> | null | undefined,
  settingField?: string | null
) => {
  if (!child?.name) {
    return undefined
  }
  if (settingField) {
    return formValues?.[`${settingField}_setting`]?.[child.name]
  }
  return formValues?.[child.name]
}

const getRowIdentity = (row: any, getRowId: (row: any) => number | null): string => {
  const id = getRowId(row)
  const settingMappingId = toNumericId(row?.setting_mapping_id)
  const isDefault =
    row?.isDefault === true ? '1' : row?.isDefault === false ? '0' : 'x'
  return `${id ?? 'x'}|${settingMappingId ?? 'x'}|${isDefault}|${row?.name ?? ''}`
}

const siblingCandidatesExcludingSelf = (
  row: any,
  context: DropdownVisibilityContext
): any[] => {
  const rowKey = getRowIdentity(row, context.getRowId)
  return (Array.isArray(context.siblings) ? context.siblings : []).filter(
    (sibling) => getRowIdentity(sibling, context.getRowId) !== rowKey
  )
}

const resolveParentMatches = (
  candidates: any[],
  parentRef: number,
  optionId: number,
  context: DropdownVisibilityContext
): any[] | null => {
  const matches = candidates.filter((sibling) =>
    context.getSiblingLookupIds(sibling).includes(parentRef)
  )
  if (!matches.length) {
    return null
  }
  const withOption = matches.filter((sibling) => context.getOptionIds(sibling).includes(optionId))
  if (!withOption.length) {
    return null
  }
  if (withOption.length === 1) {
    return withOption
  }
  return null
}

const evaluateParentRefControl = (
  row: any,
  optionId: number,
  parentRef: number,
  context: DropdownVisibilityContext
): ControlResult => {
  const parentMatches = resolveParentMatches(
    siblingCandidatesExcludingSelf(row, context),
    parentRef,
    optionId,
    context
  )
  if (!parentMatches) {
    return 'fail'
  }
  return parentMatches.some((sibling) => context.getSelectedIds(sibling).includes(optionId))
    ? 'pass'
    : 'fail'
}

const evaluatePoolCase2Control = (
  row: any,
  optionId: number,
  context: DropdownVisibilityContext
): ControlResult => {
  const withOption = siblingCandidatesExcludingSelf(row, context).filter((sibling) =>
    context.getOptionIds(sibling).includes(optionId)
  )
  if (!withOption.length) {
    return 'fail'
  }
  return withOption.some((sibling) => context.getSelectedIds(sibling).includes(optionId))
    ? 'pass'
    : 'fail'
}

const buildMappingDropdownContext = (
  thisSideMappings: unknown[] = [],
  oppositeSideMappings: unknown[] = [],
  pendingMappings: any[] = [],
  savedMappings: any[] = []
): DropdownVisibilityContext => ({
  siblings: combineSettingControlSiblingPools(thisSideMappings, oppositeSideMappings),
  getRowId: (item) => toNumericId(item?.id),
  getOptionIds: getMappingOptionIds,
  getSelectedIds: (item) => collectMappingSelectedOptionIds(item, pendingMappings, savedMappings),
  getParentRefId: getMappingParentRefId,
  getSiblingLookupIds: getMappingSiblingLookupIds,
})

const evaluateMappingTrackControl = (
  row: any,
  control: SettingControlLike,
  context: DropdownVisibilityContext
): ControlResult => {
  const optionId = toNumericId(control?.belongs_to_platform_field_data)
  if (optionId === null) {
    return 'skip'
  }

  const parentRef = getMappingParentRefId(control)
  if (parentRef !== null) {
    return evaluateParentRefControl(row, optionId, parentRef, context)
  }

  if (toNumericId(control?.belongs_to_setting) !== null) {
    return 'fail'
  }

  return evaluatePoolCase2Control(row, optionId, context)
}

const evaluateChildTrackControl = (
  row: any,
  control: SettingControlLike,
  childContext: DropdownVisibilityContext,
  mappingContext: DropdownVisibilityContext | null
): ControlResult => {
  const optionId = toNumericId(control?.belongs_to_platform_field_data)
  if (optionId === null) {
    return 'skip'
  }

  const childParentRef = getChildParentRefId(control)
  const mappingParentRef = getMappingParentRefId(control)

  if (childParentRef !== null) {
    return evaluateParentRefControl(row, optionId, childParentRef, childContext)
  }

  if (mappingParentRef !== null && mappingContext) {
    return evaluateParentRefControl(row, optionId, mappingParentRef, mappingContext)
  }

  if (mappingParentRef !== null && !mappingContext) {
    return 'fail'
  }

  const childPoolHasOption = siblingCandidatesExcludingSelf(row, childContext).some((sibling) =>
    childContext.getOptionIds(sibling).includes(optionId)
  )
  if (childPoolHasOption) {
    return evaluatePoolCase2Control(row, optionId, childContext)
  }

  if (mappingContext) {
    return evaluatePoolCase2Control(row, optionId, mappingContext)
  }

  return 'fail'
}

const evaluateControl = (
  row: any,
  control: SettingControlLike,
  context: DropdownVisibilityContext
): ControlResult => evaluateMappingTrackControl(row, control, context)

export const aggregateSettingControlResults = (results: ControlResult[]): boolean => {
  const remaining = results.filter((result) => result !== 'skip')
  if (!remaining.length) {
    return true
  }
  if (remaining.some((result) => result === 'pass')) {
    return true
  }
  return false
}

export const shouldRenderByDropdownSettingControl = (
  row: any,
  context: DropdownVisibilityContext
): boolean => {
  if (!row) {
    return true
  }

  const results = getSettingControls(row).map((control) => evaluateControl(row, control, context))
  return aggregateSettingControlResults(results)
}

export const shouldRenderMappingByDropdownControl = (
  row: any,
  thisSideSiblings: unknown[] = [],
  pendingMappings: any[] = [],
  savedMappings: any[] = [],
  oppositeSideSiblings: unknown[] = []
): boolean =>
  shouldRenderByDropdownSettingControl(row, {
    siblings: combineSettingControlSiblingPools(thisSideSiblings, oppositeSideSiblings),
    getRowId: (item) => toNumericId(item?.id),
    getOptionIds: getMappingOptionIds,
    getSelectedIds: (item) =>
      collectMappingSelectedOptionIds(item, pendingMappings, savedMappings),
    getParentRefId: getMappingParentRefId,
    getSiblingLookupIds: getMappingSiblingLookupIds,
  })

export const shouldRenderChildByDropdownControl = (
  row: any,
  thisSideSiblings: unknown[] = [],
  formValues?: Record<string, any> | null,
  settingField?: string | null,
  oppositeSideSiblings: unknown[] = [],
  renderOptions?: ChildSettingControlRenderOptions
): boolean => {
  if (!row) {
    return true
  }

  const childContext: DropdownVisibilityContext = {
    siblings: combineSettingControlSiblingPools(thisSideSiblings, oppositeSideSiblings),
    getRowId: (item) => toNumericId(item?.id),
    getOptionIds: getChildOptionIds,
    getSelectedIds: (item) =>
      collectChildSelectedOptionIds(item, getChildFormValue(item, formValues, settingField)),
    getParentRefId: getChildParentRefId,
    getSiblingLookupIds: getChildSiblingLookupIds,
  }

  const mappingContext = renderOptions
    ? buildMappingDropdownContext(
        renderOptions.thisSideMappings ?? [],
        renderOptions.oppositeSideMappings ?? [],
        renderOptions.pendingMappings ?? [],
        renderOptions.savedMappings ?? []
      )
    : null

  const results = getSettingControls(row).map((control) =>
    evaluateChildTrackControl(row, control, childContext, mappingContext)
  )
  return aggregateSettingControlResults(results)
}
